import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { Search, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { vendorService } from '../services/vendorService';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';

const ProcurementVendors = () => {
  const navigate = useNavigate();
  const DEFAULT_COL_WIDTHS = { vendorNo: 90, name: 220, contact: 160, phone: 140, email: 220, city: 130, status: 100 };
  const [searchTerm, setSearchTerm] = useState('');
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [sortKey, setSortKey] = useState('vendor_number');
  const [sortDirection, setSortDirection] = useState('asc');
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_procurement_vendors_col_widths',
    minWidths: { vendorNo: 70, name: 140, contact: 110, phone: 100, email: 140, city: 90, status: 90 }
  });

  const loadVendors = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await vendorService.getAll({
        search: searchTermRef.current || undefined,
        activeOnly: !showInactive,
        limit: 500
      });
      setVendors(res?.data ?? []);
    } catch (err) {
      console.error('Error loading vendors:', err);
      setError(err.response?.data?.message || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const handleSearch = () => {
    loadVendors();
  };

  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedVendors = useMemo(() => {
    const list = [...vendors];
    const val = (row) => {
      switch (sortKey) {
        case 'vendor_number':
          return row.vendor_number ?? row.id ?? 0;
        case 'name':
          return row.name ?? '';
        case 'contact_name':
          return row.contact_name ?? '';
        case 'phone':
          return row.phone ?? '';
        case 'email':
          return row.email ?? '';
        case 'city':
          return row.city ?? '';
        case 'is_active':
          return row.is_active ? 1 : 0;
        default:
          return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [vendors, sortKey, sortDirection]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Vendors
        </Typography>
        <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={() => navigate('/procurement/vendors/new')}>
          New
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by name, contact, or vendor #..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" onClick={handleSearch}>
                    <Search />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 260 }}
          />
          <Button variant="outlined" size="small" onClick={handleSearch}>
            Search
          </Button>
          <Button variant="outlined" size="small" onClick={() => setShowInactive((s) => !s)}>
            {showInactive ? 'Active only' : 'Show inactive'}
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <ResizableHeaderCell
                width={colWidths.vendorNo}
                onResizeStart={(e) => startResize('vendorNo', e)}
                sortable
                sortActive={sortKey === 'vendor_number'}
                sortDirection={sortDirection}
                onSort={() => handleSort('vendor_number')}
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  #
                </Typography>
              </ResizableHeaderCell>
              <ResizableHeaderCell
                width={colWidths.name}
                onResizeStart={(e) => startResize('name', e)}
                sortable
                sortActive={sortKey === 'name'}
                sortDirection={sortDirection}
                onSort={() => handleSort('name')}
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  Name
                </Typography>
              </ResizableHeaderCell>
              <ResizableHeaderCell
                width={colWidths.contact}
                onResizeStart={(e) => startResize('contact', e)}
                sortable
                sortActive={sortKey === 'contact_name'}
                sortDirection={sortDirection}
                onSort={() => handleSort('contact_name')}
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  Contact
                </Typography>
              </ResizableHeaderCell>
              <ResizableHeaderCell
                width={colWidths.phone}
                onResizeStart={(e) => startResize('phone', e)}
                sortable
                sortActive={sortKey === 'phone'}
                sortDirection={sortDirection}
                onSort={() => handleSort('phone')}
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  Phone
                </Typography>
              </ResizableHeaderCell>
              <ResizableHeaderCell
                width={colWidths.email}
                onResizeStart={(e) => startResize('email', e)}
                sortable
                sortActive={sortKey === 'email'}
                sortDirection={sortDirection}
                onSort={() => handleSort('email')}
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  Email
                </Typography>
              </ResizableHeaderCell>
              <ResizableHeaderCell
                width={colWidths.city}
                onResizeStart={(e) => startResize('city', e)}
                sortable
                sortActive={sortKey === 'city'}
                sortDirection={sortDirection}
                onSort={() => handleSort('city')}
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  City
                </Typography>
              </ResizableHeaderCell>
              <ResizableHeaderCell
                width={colWidths.status}
                onResizeStart={(e) => startResize('status', e)}
                sortable
                sortActive={sortKey === 'is_active'}
                sortDirection={sortDirection}
                onSort={() => handleSort('is_active')}
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  Status
                </Typography>
              </ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : vendors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No vendors found. Use New to add vendors for procurement.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayedVendors.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => navigate(`/procurement/vendors/${row.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ width: colWidths.vendorNo }}>{row.vendor_number ?? row.id}</TableCell>
                  <TableCell sx={{ width: colWidths.name }}>{row.name ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.contact }}>{row.contact_name ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.phone }}>{row.phone ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.email }}>{row.email ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.city }}>{row.city ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.status }}>
                    <Chip label={row.is_active !== false ? 'Active' : 'Inactive'} size="small" color={row.is_active !== false ? 'success' : 'default'} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProcurementVendors;
